/**
 * Swatch row under a colour variant product card.
 *
 * Each card is rendered for one colour, but its gallery carries a slide for every
 * colour of the product, keyed by media id. Picking a swatch re-points the card at
 * that colour: image, colour name, price, and every link out of the card.
 *
 * Without JavaScript each swatch stays what it is in the markup, a link to that
 * colour's variant URL, so the row still works.
 */
class ColourCardSwatches extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('pointerenter', this.#handlePointerEnter, true);
    this.addEventListener('pointerleave', this.#handlePointerLeave);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('pointerenter', this.#handlePointerEnter, true);
    this.removeEventListener('pointerleave', this.#handlePointerLeave);
  }

  /** @returns {Element | null} */
  get #card() {
    return this.closest('product-card');
  }

  /**
   * @param {Event} event
   * @returns {HTMLAnchorElement | null}
   */
  #swatchFrom(event) {
    const target = event.target;
    if (!(target instanceof Element)) return null;
    return target.closest('.colour-variant-card__swatch-link');
  }

  /** @returns {HTMLAnchorElement | null} */
  get #selected() {
    return this.querySelector('.colour-variant-card__swatch-link[aria-current="true"]');
  }

  /**
   * Shows a colour's image without changing what the card is set to.
   * @param {string | undefined} mediaId
   */
  #showMedia(mediaId) {
    const card = this.#card;
    if (!mediaId || !card) return;
    // previewVariant is ProductCard's own slideshow selection, so the card keeps
    // using the theme's slideshow rather than a second implementation here.
    if (typeof (/** @type {any} */ (card).previewVariant) === 'function') {
      /** @type {any} */ (card).previewVariant(mediaId);
    }
  }

  /** @param {Event} event */
  #handlePointerEnter = (event) => {
    if (event instanceof PointerEvent && event.pointerType !== 'mouse') return;
    const swatch = this.#swatchFrom(event);
    if (swatch) this.#showMedia(swatch.dataset.mediaId);
  };

  /** @param {Event} event */
  #handlePointerLeave = (event) => {
    if (event instanceof PointerEvent && event.pointerType !== 'mouse') return;
    // Fall back to whatever the card is actually set to.
    this.#showMedia(this.#selected?.dataset.mediaId);
  };

  /** @param {MouseEvent | Event} event */
  #handleClick = (event) => {
    const swatch = this.#swatchFrom(event);
    if (!swatch) return;

    // Let a modified click open the colour in a new tab as a normal link would.
    if (
      event instanceof MouseEvent &&
      (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0)
    ) {
      return;
    }

    event.preventDefault();
    // Stop the card's own overlay link and view transition handler from firing.
    event.stopPropagation();
    this.select(swatch);
  };

  /**
   * Re-points the card at a colour.
   * @param {HTMLAnchorElement} swatch
   */
  select(swatch) {
    const card = this.#card;
    if (!card) return;

    this.#showMedia(swatch.dataset.mediaId);

    const label = card.querySelector('[data-colour-label]');
    if (label && swatch.dataset.swatchColour) label.textContent = swatch.dataset.swatchColour;

    const price = card.querySelector('[data-colour-price]');
    if (price && swatch.dataset.price) price.textContent = swatch.dataset.price;

    const href = swatch.getAttribute('href');
    if (href) {
      for (const link of card.querySelectorAll('a[ref="productCardLink"], a[ref="cardGalleryLink"], a[ref="productTitleLink"]')) {
        if (link instanceof HTMLAnchorElement) link.href = href;
      }
    }

    if (swatch.dataset.variantId) card.setAttribute('data-variant-id', swatch.dataset.variantId);
    if (swatch.dataset.swatchColour) card.setAttribute('data-colour', swatch.dataset.swatchColour);

    for (const other of this.querySelectorAll('.colour-variant-card__swatch-link[aria-current]')) {
      other.removeAttribute('aria-current');
    }
    swatch.setAttribute('aria-current', 'true');
  }
}

if (!customElements.get('colour-card-swatches')) {
  customElements.define('colour-card-swatches', ColourCardSwatches);
}
